import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { IdCard, ShieldCheck, Search, CheckCircle, XCircle } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import api from '@/lib/api';

export default function VerifyCard() {
  const { cardNumber } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [search, setSearch] = useState(cardNumber || '');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  React.useEffect(() => {
    if (cardNumber) {
      doVerify(cardNumber);
    }
  }, [cardNumber]);

  const doVerify = async (num) => {
    setLoading(true);
    setError(false);
    setResult(null);
    setSearched(true);
    try {
      const r = await api.get(`/cards/verify/${encodeURIComponent(num)}`);
      setResult(r.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (search.trim()) doVerify(search.trim());
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg space-y-6 animate-fade-in">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-[#007FFF]/10 flex items-center justify-center mb-4">
            <ShieldCheck className="w-8 h-8 text-[#007FFF]" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('verify.title')}
          </h1>
        </div>

        <Card className="border" data-testid="verify-card-form">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={t('verify.enter')}
                  className="pl-10 font-mono"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  data-testid="verify-input"
                />
              </div>
              <Button type="submit" className="bg-[#007FFF] hover:bg-[#0066CC]" disabled={loading} data-testid="verify-btn">
                {t('verify.btn')}
              </Button>
            </form>
          </CardContent>
        </Card>

        {searched && result && (
          <Card className="border border-[#009543]/30 bg-[#009543]/5" data-testid="verify-result-valid">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="w-8 h-8 text-[#009543]" />
                <div>
                  <h2 className="text-lg font-bold text-[#009543]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t('verify.valid')}
                  </h2>
                  <p className="text-sm font-mono text-muted-foreground">{result.card_number}</p>
                </div>
                <Badge className="ml-auto bg-[#009543] text-white">{t('verify.active')}</Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">{t('verify.name')}</p>
                  <p className="font-semibold">{result.full_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">{t('verify.nationality')}</p>
                  <p className="font-medium">{result.nationality}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">{t('verify.residence')}</p>
                  <p className="font-medium">{result.country_of_residence}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">{t('verify.issued')}</p>
                  <p className="font-medium">{result.issued_at ? new Date(result.issued_at).toLocaleDateString('fr-FR') : '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">{t('verify.expires')}</p>
                  <p className="font-medium">{result.expires_at ? new Date(result.expires_at).toLocaleDateString('fr-FR') : '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {searched && error && (
          <Card className="border border-[#CE1021]/30 bg-[#CE1021]/5" data-testid="verify-result-invalid">
            <CardContent className="p-6 text-center">
              <XCircle className="w-10 h-10 text-[#CE1021] mx-auto mb-3" />
              <h2 className="text-lg font-bold text-[#CE1021]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('verify.invalid')}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">{t('verify.invalid_desc')}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
